import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useEffect, useState } from "react";
import { format } from "date-fns";
import { CalendarIcon, Languages, Loader2, Plus, Save, Trash2 } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { cn } from "@/lib/utils";
import { MarkdownEditor } from "@/components/admin/MarkdownEditor";
import { ImageUploadField } from "@/components/admin/ImageUploadField";
import { supabase } from "@/integrations/supabase/client";
import { youtubeId } from "@/lib/youtube";
import {
  adminAutoTranslate,
  adminGetContent,
  adminListIndustryProducts,
  adminSaveContent,
  adminSaveIndustryProducts,
  adminSaveTranslation,
} from "@/lib/admin.functions";
import { getAddons } from "@/lib/pricing.functions";
import { MODULE_CATALOG } from "@/content/modules";
import { CONTENT_KINDS, type ContentKind } from "@/lib/cms.types";
import { LOCALES, LOCALE_NAMES, type Locale } from "@/lib/i18n";

type TranslationDraft = {
  slug: string;
  title: string;
  excerpt: string;
  body: string;
  seo_title: string;
  seo_description: string;
};

type ProductDraft = { key: string; pain: string; outcome: string };

const EMPTY: TranslationDraft = {
  slug: "",
  title: "",
  excerpt: "",
  body: "",
  seo_title: "",
  seo_description: "",
};

export const Route = createFileRoute("/admin/content/$kind/$id")({
  staticData: { sitemap: false },
  component: ContentEditor,
});

function ContentEditor() {
  const { kind, id } = Route.useParams();
  const isNew = id === "new";
  const typedKind = (CONTENT_KINDS as readonly string[]).includes(kind)
    ? (kind as ContentKind)
    : "blog";
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const load = useServerFn(adminGetContent);
  const saveItem = useServerFn(adminSaveContent);
  const saveTranslation = useServerFn(adminSaveTranslation);
  const autoTranslate = useServerFn(adminAutoTranslate);

  const { data, isLoading } = useQuery({
    queryKey: ["admin-item", id],
    queryFn: () => load({ data: { id } }),
    enabled: !isNew,
  });

  const [slug, setSlug] = useState("");
  const [coverUrl, setCoverUrl] = useState("");
  const [videoUrl, setVideoUrl] = useState("");
  const [gallery, setGallery] = useState<{ url: string; caption?: string }[]>([]);
  const [published, setPublished] = useState(false);
  const [publishedAt, setPublishedAt] = useState<Date | undefined>(undefined);
  const [sortOrder, setSortOrder] = useState(0);
  const [locale, setLocale] = useState<Locale>("en");
  const [drafts, setDrafts] = useState<Record<string, TranslationDraft>>({});
  const [products, setProducts] = useState<ProductDraft[]>([]);

  const isIndustry = typedKind === "industry";
  const isBlog = typedKind === "blog";
  const [categoryIds, setCategoryIds] = useState<string[]>([]);
  const { data: categoryOptions } = useQuery({
    queryKey: ["admin-blog-categories-options"],
    enabled: isBlog,
    queryFn: async () => {
      const { data: rows, error } = await supabase
        .from("blog_categories")
        .select("id, slug, names")
        .order("sort_order");
      if (error) throw error;
      return (rows ?? []).map((r) => ({
        id: r.id,
        name: ((r.names ?? {}) as Record<string, string>)["en"] || r.slug,
      }));
    },
  });
  const { data: linkedCategories } = useQuery({
    queryKey: ["admin-item-categories", id],
    enabled: isBlog && !isNew,
    queryFn: async () => {
      const { data: rows, error } = await supabase
        .from("content_item_categories")
        .select("category_id")
        .eq("item_id", id);
      if (error) throw error;
      return (rows ?? []).map((r) => r.category_id);
    },
  });
  useEffect(() => {
    if (linkedCategories) setCategoryIds(linkedCategories);
  }, [linkedCategories]);
  const loadProducts = useServerFn(adminListIndustryProducts);
  const saveProducts = useServerFn(adminSaveIndustryProducts);
  const loadAddons = useServerFn(getAddons);

  const { data: productRows } = useQuery({
    queryKey: ["admin-industry-products", id],
    queryFn: () => loadProducts({ data: { item_id: id } }),
    enabled: !isNew && isIndustry,
  });

  const { data: addons } = useQuery({
    queryKey: ["admin-addon-options"],
    queryFn: () => loadAddons(),
    enabled: isIndustry,
  });

  useEffect(() => {
    if (!productRows) return;
    setProducts(
      productRows.map((row) => ({
        key: `${row.product_type}:${row.product_ref}`,
        pain: row.pain ?? "",
        outcome: row.outcome ?? "",
      })),
    );
  }, [productRows]);

  useEffect(() => {
    if (!data) return;
    setSlug(data.slug);
    setCoverUrl(data.cover_url ?? "");
    setVideoUrl((data as { video_url?: string | null }).video_url ?? "");
    setGallery(Array.isArray((data as { gallery?: unknown }).gallery) ? ((data as unknown as { gallery: { url: string; caption?: string }[] }).gallery) : []);
    setPublished(data.published);
    setPublishedAt(data.published_at ? new Date(data.published_at) : undefined);
    setSortOrder(data.sort_order);
    const next: Record<string, TranslationDraft> = {};
    for (const translation of data.content_translations ?? []) {
      next[translation.locale] = {
        slug: translation.slug ?? "",
        title: translation.title ?? "",
        excerpt: translation.excerpt ?? "",
        body: translation.body ?? "",
        seo_title: translation.seo_title ?? "",
        seo_description: translation.seo_description ?? "",
      };
    }
    setDrafts(next);
  }, [data]);

  const draft = drafts[locale] ?? EMPTY;
  const update = (patch: Partial<TranslationDraft>) =>
    setDrafts((prev) => ({ ...prev, [locale]: { ...(prev[locale] ?? EMPTY), ...patch } }));

  const save = useMutation({
    mutationFn: async () => {
      const { id: itemId } = await saveItem({
        data: {
          ...(isNew ? {} : { id }),
          kind: typedKind,
          slug: slug.trim(),
          cover_url: coverUrl.trim() || null,
          video_url: videoUrl.trim() || null,
          gallery: gallery.filter((g) => g.url.trim()),
          published,
          published_at: publishedAt ? publishedAt.toISOString() : null,
          sort_order: sortOrder,
          metrics: [],
        },
      });
      await saveTranslation({
        data: {
          item_id: itemId,
          locale,
          slug: draft.slug.trim() || null,
          title: draft.title,
          excerpt: draft.excerpt,
          body: draft.body,
          seo_title: draft.seo_title || null,
          seo_description: draft.seo_description || null,
          translation_status: "edited",
        },
      });
      if (isBlog) {
        const { error: delError } = await supabase
          .from("content_item_categories")
          .delete()
          .eq("item_id", itemId);
        if (delError) throw delError;
        if (categoryIds.length) {
          const { error: insError } = await supabase
            .from("content_item_categories")
            .insert(categoryIds.map((category_id) => ({ item_id: itemId, category_id })));
          if (insError) throw insError;
        }
      }
      if (isIndustry) {
        await saveProducts({
          data: {
            item_id: itemId,
            products: products
              .filter((product) => product.key)
              .map((product, index) => {
                const [type, ...rest] = product.key.split(":");
                return {
                  product_type: type === "addon" ? ("addon" as const) : ("module" as const),
                  product_ref: rest.join(":"),
                  pain: product.pain,
                  outcome: product.outcome,
                  sort_order: index,
                };
              }),
          },
        });
      }
      return itemId;
    },
    onSuccess: (itemId) => {
      toast.success("Saved");
      void queryClient.invalidateQueries({ queryKey: ["admin-content", typedKind] });
      void queryClient.invalidateQueries({ queryKey: ["admin-item", itemId] });
      void queryClient.invalidateQueries({ queryKey: ["admin-industry-products", itemId] });
      if (isNew) {
        void navigate({
          to: "/admin/content/$kind/$id" as never,
          params: { kind: typedKind, id: itemId } as never,
        });
      }
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const translate = useMutation({
    mutationFn: () =>
      autoTranslate({
        data: {
          item_id: id,
          targets: LOCALES.filter((l) => l !== "en"),
          overwriteEdited: false,
        },
      }),
    onSuccess: ({ results }) => {
      const failed = results.filter((r) => !r.ok);
      toast.success(
        failed.length
          ? `Translated ${results.length - failed.length}/${results.length}. Skipped: ${failed
              .map((f) => f.locale)
              .join(", ")}`
          : "All languages translated",
      );
      void queryClient.invalidateQueries({ queryKey: ["admin-item", id] });
    },
    onError: (error: Error) => toast.error(error.message),
  });

  if (!isNew && isLoading) {
    return (
      <div className="flex justify-center py-16">
        <Loader2 className="size-6 animate-spin text-primary" aria-hidden />
      </div>
    );
  }

  return (
    <>
      <div className="flex flex-wrap items-center justify-between gap-4">
        <h1 className="font-display text-3xl font-bold">
          {isNew ? "New entry" : (drafts["en"]?.title ?? slug)}
        </h1>
        <div className="flex items-center gap-2">
          {!isNew ? (
            <Button
              variant="outline"
              onClick={() => translate.mutate()}
              disabled={translate.isPending}
            >
              {translate.isPending ? (
                <Loader2 className="size-4 animate-spin" aria-hidden />
              ) : (
                <Languages className="size-4" aria-hidden />
              )}
              Translate all
            </Button>
          ) : null}
          <Button onClick={() => save.mutate()} disabled={save.isPending}>
            {save.isPending ? (
              <Loader2 className="size-4 animate-spin" aria-hidden />
            ) : (
              <Save className="size-4" aria-hidden />
            )}
            Save
          </Button>
        </div>
      </div>

      <div className="mt-8 grid gap-8 lg:grid-cols-[1fr_280px]">
        <div>
          <Tabs value={locale} onValueChange={(value) => setLocale(value as Locale)}>
            <TabsList className="flex-wrap">
              {LOCALES.map((code) => (
                <TabsTrigger key={code} value={code}>
                  {LOCALE_NAMES[code]}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>

          <div className="mt-6 grid gap-5">
            <div className="grid gap-2">
              <Label htmlFor="title">Title</Label>
              <Input
                id="title"
                value={draft.title}
                onChange={(event) => update({ title: event.target.value })}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="localizedSlug">Localized URL slug</Label>
              <Input
                id="localizedSlug"
                value={draft.slug}
                placeholder={slug}
                onChange={(event) => update({ slug: event.target.value })}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="excerpt">Excerpt</Label>
              <Textarea
                id="excerpt"
                rows={3}
                value={draft.excerpt}
                onChange={(event) => update({ excerpt: event.target.value })}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="body">Body</Label>
              <MarkdownEditor
                key={`body-${locale}`}
                value={draft.body}
                onChange={(body) => update({ body })}
                folder={typedKind}
              />
              <p className="text-xs text-muted-foreground">
                Rich text editing. Use the “Source” toggle in the toolbar to edit raw Markdown.
              </p>
            </div>
            <div className="grid gap-5 sm:grid-cols-2">
              <div className="grid gap-2">
                <Label htmlFor="seoTitle">SEO title</Label>
                <Input
                  id="seoTitle"
                  value={draft.seo_title}
                  onChange={(event) => update({ seo_title: event.target.value })}
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="seoDescription">SEO description</Label>
                <Input
                  id="seoDescription"
                  value={draft.seo_description}
                  onChange={(event) => update({ seo_description: event.target.value })}
                />
              </div>
            </div>
          </div>

          {isIndustry ? (
            <section className="mt-10 rounded-2xl border border-border bg-card p-6">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <h2 className="font-display text-lg font-semibold">Products &amp; pains</h2>
                  <p className="mt-1 text-sm text-muted-foreground">
                    Link modules and add-ons to this industry and describe the pain each one closes.
                  </p>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    setProducts((prev) => [...prev, { key: "", pain: "", outcome: "" }])
                  }
                >
                  <Plus className="size-4" aria-hidden />
                  Add product
                </Button>
              </div>

              <div className="mt-5 grid gap-4">
                {products.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No products linked yet.</p>
                ) : null}
                {products.map((product, index) => (
                  <div key={index} className="grid gap-3 rounded-xl border border-border p-4">
                    <div className="flex items-center gap-3">
                      <select
                        value={product.key}
                        onChange={(event) =>
                          setProducts((prev) =>
                            prev.map((p, i) => (i === index ? { ...p, key: event.target.value } : p)),
                          )
                        }
                        className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                      >
                        <option value="">Select a product…</option>
                        <optgroup label="Modules">
                          {MODULE_CATALOG.map((moduleEntry) => (
                            <option key={moduleEntry.slug} value={`module:${moduleEntry.slug}`}>
                              {moduleEntry.title}
                            </option>
                          ))}
                        </optgroup>
                        <optgroup label="Add-ons">
                          {(addons ?? []).map((addon) => (
                            <option key={addon.slug} value={`addon:${addon.slug}`}>
                              {addon.name}
                            </option>
                          ))}
                        </optgroup>
                      </select>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        aria-label="Remove product"
                        onClick={() => setProducts((prev) => prev.filter((_, i) => i !== index))}
                      >
                        <Trash2 className="size-4" aria-hidden />
                      </Button>
                    </div>
                    <Textarea
                      rows={2}
                      placeholder="Pain: what hurts in this industry today"
                      value={product.pain}
                      onChange={(event) =>
                        setProducts((prev) =>
                          prev.map((p, i) => (i === index ? { ...p, pain: event.target.value } : p)),
                        )
                      }
                    />
                    <Textarea
                      rows={2}
                      placeholder="Outcome: how this product closes that pain"
                      value={product.outcome}
                      onChange={(event) =>
                        setProducts((prev) =>
                          prev.map((p, i) =>
                            i === index ? { ...p, outcome: event.target.value } : p,
                          ),
                        )
                      }
                    />
                  </div>
                ))}
              </div>
            </section>
          ) : null}
        </div>

        <aside className="grid h-fit gap-5 rounded-2xl border border-border bg-card p-6">
          <div className="grid gap-2">
            <Label htmlFor="slug">Slug</Label>
            <Input id="slug" value={slug} onChange={(event) => setSlug(event.target.value)} />
          </div>
          <ImageUploadField
            id="cover"
            label="Cover image"
            value={coverUrl}
            onChange={setCoverUrl}
            folder={typedKind}
          />
          <div className="grid gap-2">
            <Label htmlFor="video">Video (YouTube link)</Label>
            <Input id="video" placeholder="https://www.youtube.com/watch?v=…" value={videoUrl} onChange={(e) => setVideoUrl(e.target.value)} />
            {videoUrl.trim() && !youtubeId(videoUrl) ? <p className="text-xs text-destructive">Not a recognised YouTube link.</p> : null}
          </div>
          <div className="grid gap-3">
            <Label>Gallery</Label>
            {gallery.map((g, i) => (
              <div key={i} className="grid gap-2 rounded-lg border border-border p-3">
                <ImageUploadField id={`gallery-${i}`} label={`Image ${i + 1}`} value={g.url} onChange={(url) => setGallery((prev) => prev.map((x, j) => (j === i ? { ...x, url } : x)))} folder={typedKind} />
                <Input placeholder="Caption (optional)" value={g.caption ?? ""} onChange={(e) => setGallery((prev) => prev.map((x, j) => (j === i ? { ...x, caption: e.target.value } : x)))} />
                <div className="flex gap-2">
                  <Button type="button" size="sm" variant="outline" disabled={i === 0} onClick={() => setGallery((prev) => { const n = [...prev]; n.splice(i - 1, 2, n[i]!, n[i - 1]!); return n; })}>↑</Button>
                  <Button type="button" size="sm" variant="outline" disabled={i === gallery.length - 1} onClick={() => setGallery((prev) => { const n = [...prev]; n.splice(i, 2, n[i + 1]!, n[i]!); return n; })}>↓</Button>
                  <Button type="button" size="sm" variant="ghost" onClick={() => setGallery((prev) => prev.filter((_, j) => j !== i))}><Trash2 className="size-4" /></Button>
                </div>
              </div>
            ))}
            <Button type="button" variant="outline" size="sm" onClick={() => setGallery((prev) => [...prev, { url: "" }])}><Plus className="size-4" /> Add image</Button>
          </div>
          {isBlog ? (
            <div className="grid gap-2">
              <Label>Categories</Label>
              {(categoryOptions ?? []).length === 0 ? (
                <p className="text-xs text-muted-foreground">No categories yet.</p>
              ) : null}
              {(categoryOptions ?? []).map((cat) => (
                <label key={cat.id} className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={categoryIds.includes(cat.id)}
                    onChange={(e) =>
                      setCategoryIds((prev) =>
                        e.target.checked ? [...prev, cat.id] : prev.filter((x) => x !== cat.id),
                      )
                    }
                    className="size-4 accent-primary"
                  />
                  {cat.name}
                </label>
              ))}
            </div>
          ) : null}
          <div className="grid gap-2">
            <Label htmlFor="sort">Sort order</Label>
            <Input
              id="sort"
              type="number"
              value={sortOrder}
              onChange={(event) => setSortOrder(Number(event.target.value) || 0)}
            />
          </div>
          <div className="grid gap-2">
            <Label>Publication date</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={cn(
                    "justify-start text-left font-normal",
                    !publishedAt && "text-muted-foreground",
                  )}
                >
                  <CalendarIcon className="size-4" aria-hidden />
                  {publishedAt ? format(publishedAt, "PPP") : <span>Pick a date</span>}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={publishedAt}
                  onSelect={setPublishedAt}
                  initialFocus
                  className="pointer-events-auto p-3"
                />
              </PopoverContent>
            </Popover>
          </div>
          <div className="flex items-center justify-between">
            <Label htmlFor="published">Published</Label>
            <Switch id="published" checked={published} onCheckedChange={setPublished} />
          </div>
          <p className="text-xs text-muted-foreground">
            Saving stores the currently selected language. Write English first, then use
            &ldquo;Translate all&rdquo;.
          </p>
        </aside>
      </div>
    </>
  );
}
